import type { OrderDetailResponseDto } from "Infrastructure/Api/Api";
import { OrderComments } from "Order/Components/Comments/OrderComments";
import { OrderPaymentTabs } from "Order/Modules/Payment/Components/Tabs/OrderPaymentTabs";
import { usePayment } from "Order/Modules/Payment/Hooks/usePayment";
import { CrossOrderNettingWidget } from "Order/Modules/Payment/Widgets/CrossOrderNettingWidget";
import { DistributionWidget } from "Order/Modules/Payment/Widgets/DistributionWidget";
import { ExpressPayoutWidget } from "Order/Modules/Payment/Widgets/ExpressPayoutWidget";
import { FeeFormWidget } from "Order/Modules/Payment/Widgets/FeeFormWidget";
import { PaymentProgressWidget } from "Order/Modules/Payment/Widgets/PaymentProgressWidget";
import { useGetOrderPriceByOrderIdQuery } from "Order/Modules/Price/Api/Query/useGetOrderPriceByOrderIdQuery";
import { Loading } from "Shared/Components/Loading/Loading";
import { Grid2 } from "@mui/material";

type OrderPaymentTabProps = {
	order: OrderDetailResponseDto;
};

/**
 * Payment / Balance tab in Order
 */
export function OrderPaymentTab({ order }: OrderPaymentTabProps) {
	const { isLoading, expressPayoutSent } = usePayment(order.id);
	const { data: orderPrice, isLoading: isLoadingOrderPrice } =
		useGetOrderPriceByOrderIdQuery(order.id);

	if (isLoading || isLoadingOrderPrice) {
		return <Loading />;
	}

	if (!order.market?.currency || !order.market.country || !orderPrice) {
		return null;
	}

	return (
		<Grid2 container spacing={2} alignItems="flex-start">
			<Grid2
				size={{ xs: 12, md: 8 }}
				container
				spacing={2}
				alignItems="flex-start"
			>
				<OrderPaymentTabs order={order} />
				<Grid2 size={{ xs: 12 }}>
					<FeeFormWidget order={order} />
				</Grid2>
				<Grid2 size={{ xs: 12 }}>
					<OrderComments id={order.id} />
				</Grid2>
			</Grid2>
			<Grid2
				size={{ xs: 12, md: 4 }}
				container
				spacing={2}
				alignItems="flex-start"
			>
				<Grid2 size={{ xs: 12 }}>
					<CrossOrderNettingWidget
						currency={order.market.currency}
						country={order.market.country}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6, md: 12 }}>
					<PaymentProgressWidget
						orderPrice={orderPrice}
						currency={order.market.currency}
						country={order.market.country}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6, md: 12 }}>
					<DistributionWidget
						orderId={order.id}
						currency={order.market.currency}
						country={order.market.country}
						orderPrice={orderPrice}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6, md: 12 }}>
					<ExpressPayoutWidget
						orderId={order.id}
						expressPayoutSent={expressPayoutSent}
					/>
				</Grid2>
			</Grid2>
		</Grid2>
	);
}
